import { useQueryClient } from '@tanstack/react-query'
import { onValue, ref, Unsubscribe } from 'firebase/database'
import { createContext, useContext, useEffect } from 'react'

import { database } from '../config/firebase'
import { setPersonaPresence } from '../services/cache/personaPresence'

const presenceContext = createContext<{
  addPresence: (personaId: string) => void
  removePresence: (personaId: string) => void
}>({
  addPresence: () => {},
  removePresence: () => {}
})

export const usePresenceContext = () => useContext(presenceContext)

export function PresenceProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()

  const presenceMap = new Map<
    string,
    { referenceAmount: number; unsubscribe: Unsubscribe }
  >()

  function addPresence(personaId: string) {
    const storedPresence = presenceMap.get(personaId)
    if (storedPresence) {
      presenceMap.set(personaId, {
        ...storedPresence,
        referenceAmount: storedPresence.referenceAmount + 1
      })
      return
    }

    const presencePath = `presence/${personaId}`
    const presenceRef = ref(database, presencePath)
    const unsubscribePresenceCallback = onValue(presenceRef, (snapshot) => {
      const presence = snapshot.val()
      setPersonaPresence(
        {
          personaId: personaId,
          presence: presence
        },
        queryClient
      )
    })
    presenceMap.set(presencePath, {
      referenceAmount: 1,
      unsubscribe: unsubscribePresenceCallback
    })
  }

  function removePresence(personaId: string) {
    const storedPresence = presenceMap.get(personaId)
    if (!storedPresence) return
    if (storedPresence.referenceAmount > 1) {
      presenceMap.set(personaId, {
        ...storedPresence,
        referenceAmount: storedPresence.referenceAmount - 1
      })
      return
    }
    storedPresence.unsubscribe()
    presenceMap.delete(personaId)
  }

  useEffect(() => {
    return () => {
      presenceMap.forEach(({ unsubscribe }) => {
        unsubscribe()
      })
    }
  }, [])

  return (
    <presenceContext.Provider value={{ addPresence, removePresence }}>
      {children}
    </presenceContext.Provider>
  )
}
